"use client";

import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";
import { CardColorEclipse } from "./composites/card-color-eclipse";

const cardOuterVariants = cva("w-full", {
  variants: {
    size: {
      compact: "max-w-[400px]",
      default: "max-w-[440px]",
      full: "max-w-full"
    }
  },
  defaultVariants: { size: "default" }
});

const cardContentVariants = cva("relative", {
  variants: {
    size: {
      compact: "px-5 pb-5 pt-3 sm:px-6 sm:pb-6 sm:pt-4",
      default: "px-8 pb-8 pt-6 sm:px-10 sm:pb-10 sm:pt-8",
      full: "px-8 pb-8 pt-6 sm:px-10 sm:pb-10 sm:pt-8"
    },
    padding: {
      default: "",
      xs: "!p-2",
      sm: "px-6 pb-6 pt-5 sm:px-7 sm:pb-7 sm:pt-6",
      comfortable: "px-4 pb-4 pt-4 sm:px-5 sm:pb-5 sm:pt-5",
      none: "!p-0"
    }
  },
  defaultVariants: {
    size: "default",
    padding: "default"
  }
});

const cardSurfaceVariants = cva(
  "relative overflow-hidden border bg-white dark:bg-zinc-900",
  {
    variants: {
      variant: {
        default: "border-border/80 rounded-[22px] dark:border-zinc-800",
        explore:
          "rounded-2xl border-sky-100/80 transition-all duration-300 dark:border-zinc-700",
        glow: "border-border/80 rounded-[22px] dark:border-zinc-800",
        guide: "border-border/80 rounded-[22px] dark:border-zinc-800",
        gradient:
          "rounded-2xl border-slate-200/80 bg-gradient-to-b from-slate-50 via-white to-white dark:border-zinc-700 dark:from-zinc-900 dark:via-zinc-900 dark:to-zinc-900"
      },
      tone: {
        default: "",
        muted: "bg-muted dark:bg-muted/40"
      }
    },
    defaultVariants: {
      variant: "default",
      tone: "default"
    }
  }
);

export interface CardProps
  extends VariantProps<typeof cardOuterVariants>,
    VariantProps<typeof cardSurfaceVariants>,
    VariantProps<typeof cardContentVariants> {
  children: React.ReactNode;
  footer?: React.ReactNode;
  entranceAnimation?: boolean;
  animatedBorder?: boolean;
  innerGlow?: boolean;
  shadow?: "none" | "default" | "elevated" | "subtle";
  borderColor?: string;
  fullHeight?: boolean;
  className?: string;
}

export function Card({
  children,
  footer,
  size = "default",
  variant = "default",
  tone = "default",
  padding = "default",
  entranceAnimation = false,
  animatedBorder = false,
  innerGlow = false,
  shadow = "default",
  borderColor,
  fullHeight = false,
  className
}: CardProps) {
  const shadowClass =
    variant === "glow" && shadow === "default"
      ? "shadow-[0_0_0_1px_rgba(15,23,42,0.06),0_4px_6px_-1px_rgba(0,0,0,0.04),0_18px_48px_-14px_rgba(15,23,42,0.09),0_6px_18px_-6px_rgba(0,0,0,0.06)]"
      : shadow === "none"
        ? ""
        : shadow === "elevated"
          ? "shadow-[0_0_0_1px_rgba(15,23,42,0.06),0_4px_6px_-1px_rgba(0,0,0,0.04),0_18px_48px_-14px_rgba(15,23,42,0.09),0_6px_18px_-6px_rgba(0,0,0,0.06)]"
          : shadow === "subtle"
            ? "shadow-[0_2px_8px_-3px_rgba(0,0,0,0.08),0_1px_2px_-1px_rgba(0,0,0,0.04)]"
            : "shadow-[0_1px_3px_rgba(0,0,0,0.045),0_6px_18px_-8px_rgba(0,0,0,0.09)]";

  const borderlessClass =
    shadow === "subtle" ? "!border-slate-200/80 dark:!border-zinc-800/60" : "";

  const exploreShadowClass =
    variant === "explore"
      ? "shadow-[0_12px_32px_-16px_rgba(14,116,220,0.35)] hover:shadow-[0_18px_45px_-18px_rgba(14,116,220,0.45)]"
      : "";
  const toneSurfaceClass =
    tone === "muted"
      ? "bg-gray-50 dark:bg-gray-900"
      : "bg-white dark:bg-zinc-900";
  const useAnimatedBorder = variant === "glow" || animatedBorder;
  const useInnerGlow = variant === "glow" || innerGlow;
  const isGradientVariant = variant === "gradient";

  return (
    <div
      className={cn(
        cardOuterVariants({ size }),
        fullHeight && "h-full",
        useAnimatedBorder && "overflow-visible",
        entranceAnimation &&
          "motion-safe:duration-700 motion-safe:ease-out motion-safe:animate-in motion-safe:fade-in-0 motion-safe:zoom-in-95 motion-safe:slide-in-from-bottom-4",
        className
      )}
    >
      {useAnimatedBorder ? (
        <div className="relative isolate overflow-visible">
          <CardColorEclipse entranceAnimation={entranceAnimation} />
          <div
            data-card-surface="true"
            className={cn(
              "relative z-10 overflow-hidden rounded-[24px] border border-slate-200/60 dark:border-zinc-800/80",
              toneSurfaceClass,
              shadowClass,
              borderlessClass,
              entranceAnimation &&
                "motion-safe:duration-700 motion-safe:ease-out motion-safe:animate-in motion-safe:fade-in-0 motion-safe:zoom-in-95"
            )}
          >
            {useInnerGlow && (
              <div
                aria-hidden
                className="pointer-events-none absolute inset-x-0 top-0 h-24 bg-[radial-gradient(ellipse_at_50%_0%,rgba(167,139,250,0.14)_0%,transparent_70%)] opacity-60 dark:opacity-30"
              />
            )}
            <div className={cardContentVariants({ size, padding })}>
              {children}
            </div>
          </div>
        </div>
      ) : (
        <div
          data-card-surface="true"
          className={cn(
            cardSurfaceVariants({ variant, tone }),
            shadowClass,
            !borderColor && borderlessClass,
            exploreShadowClass,
            fullHeight && "flex h-full flex-col"
          )}
          style={borderColor ? { borderColor } : undefined}
        >
          {useInnerGlow && (
            <div
              aria-hidden
              className="pointer-events-none absolute inset-x-0 top-0 h-24 bg-[radial-gradient(ellipse_at_50%_0%,rgba(167,139,250,0.14)_0%,transparent_70%)] opacity-60 dark:opacity-30"
            />
          )}
          {isGradientVariant && (
            <div aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-36 overflow-hidden">
              <div
                className="absolute -left-8 -top-10 h-24 w-24 rounded-full blur-2xl"
                style={{
                  background:
                    "radial-gradient(circle, rgba(34,211,238,0.34) 0%, rgba(34,211,238,0.12) 42%, transparent 74%)",
                }}
              />
              <div
                className="absolute -right-8 -top-10 h-24 w-24 rounded-full blur-2xl"
                style={{
                  background:
                    "radial-gradient(circle, rgba(251,146,60,0.3) 0%, rgba(251,146,60,0.1) 42%, transparent 74%)",
                }}
              />
              <div
                className="absolute left-1/2 -top-4 h-20 w-28 -translate-x-1/2 rounded-full blur-2xl"
                style={{
                  background:
                    "radial-gradient(circle, rgba(167,139,250,0.26) 0%, rgba(99,102,241,0.1) 48%, transparent 74%)",
                }}
              />
              <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-b from-transparent via-white/65 to-white dark:via-zinc-900/70 dark:to-zinc-900" />
            </div>
          )}
          <div
            className={cn(
              cardContentVariants({ size, padding }),
              fullHeight && "flex flex-1 flex-col"
            )}
          >
            {children}
          </div>
        </div>
      )}
      {footer}
    </div>
  );
}

export interface CardHeaderProps {
  logo?: React.ReactNode;
  title: React.ReactNode;
  description?: React.ReactNode;
  align?: "center" | "left";
  compact?: boolean;
}

export function CardHeader({
  logo,
  title,
  description,
  align = "center",
  compact = false
}: CardHeaderProps) {
  return (
    <header
      className={cn(
        "flex flex-col gap-1",
        compact ? "mb-2.5 gap-0.5" : "mb-4 gap-1.5",
        align === "center"
          ? "items-center text-center"
          : "items-start text-left"
      )}
    >
      {logo && <div className={compact ? "mb-1" : "mb-3"}>{logo}</div>}
      <h1
        className={cn(
          "text-slate-900 dark:text-white",
          compact
            ? "text-lg font-semibold tracking-tight sm:text-xl"
            : "text-xl font-bold tracking-tight sm:text-2xl"
        )}
      >
        {title}
      </h1>
      {description && (
        <p
          className={cn(
            "text-slate-500 dark:text-zinc-400",
            compact
              ? "max-w-[260px] text-xs leading-relaxed"
              : "max-w-[280px] text-[13px] leading-snug"
          )}
        >
          {description}
        </p>
      )}
    </header>
  );
}

export interface CardBodyProps {
  children: React.ReactNode;
}

export function CardBody({ children }: CardBodyProps) {
  return <div className="space-y-3">{children}</div>;
}

export interface CardFooterProps {
  children: React.ReactNode;
}

export function CardFooter({ children }: CardFooterProps) {
  return (
    <p className="mt-3 text-center text-[10px] tracking-wide text-slate-400 dark:text-zinc-600">
      {children}
    </p>
  );
}

export interface CardWelcomeProps {
  emoji?: React.ReactNode;
  title: React.ReactNode;
  description: React.ReactNode;
  action: React.ReactNode;
}

export function CardWelcome({
  emoji = "✨",
  title,
  description,
  action
}: CardWelcomeProps) {
  return (
    <div className="flex flex-col items-center gap-4 py-1 text-center">
      <div className="flex h-14 w-14 items-center justify-center rounded-2xl border border-violet-500/15 bg-gradient-to-br from-violet-500/10 to-cyan-500/10 text-2xl shadow-sm">
        {emoji}
      </div>
      <div className="space-y-1.5">
        <h2 className="text-md font-bold text-slate-900 dark:text-white">
          {title}
        </h2>
        <p className="text-[13px] leading-snug text-slate-500 dark:text-zinc-400">
          {description}
        </p>
      </div>
      {action}
    </div>
  );
}
